import type {
  ProjectDepartment,
  ProjectPhase,
  ProjectTask,
  TaskGroup,
  TaskPriority,
  TaskStatus,
  TimberProject,
} from '../types';
import { DEPARTMENT_DISPLAY_LABELS, DEPARTMENT_SWATCH_COLORS, PROJECT_PHASE_LABELS } from '../types';
import { isInstallGroupName, isProductionGroupName } from './paymentDueFlags';
import {
  resolveCanonicalTimelineColor,
  type GroupTimelineColorEntry,
} from './groupTimelineCanonicalColors';
import {
  MULTIPLE_SUPPLIER_KEY,
  UNASSIGNED_SUPPLIER_KEY,
  UNASSIGNED_SUPPLIER_LABEL,
  orgNameFromMap,
  parseSupplierGroupId,
  primarySupplierForTasks,
  supplierDisplayTaskId,
  supplierGroupId,
  unwrapSupplierDisplayTaskId,
  type GroupTimelineSupplierResolution,
} from './groupTimelineSupplier';

export const GROUP_TIMELINE_FAMILIES = [
  'all',
  'procurement',
  'production',
  'installation',
] as const;

export type GroupTimelineFamily = (typeof GROUP_TIMELINE_FAMILIES)[number];

export const GROUP_TIMELINE_FAMILY_LABELS: Record<GroupTimelineFamily, string> = {
  all: 'All',
  procurement: 'Procurement',
  production: 'Production',
  installation: 'Installation',
};

export function isAllGroupTimelineFamily(
  family: GroupTimelineFamily | null | undefined,
): family is 'all' {
  return family === 'all';
}

/** Major-group bands used on the All-projects timeline. */
export type GroupTimelineSection = {
  id: string;
  name: string;
  color: string;
  groupIds: string[];
};

const GROUP_TIMELINE_SECTION_COLORS: Record<string, string> = {
  procurement: '#0D9488',
  production: '#D97706',
  installation: '#2563EB',
};

const FAMILY_SECTION_ORDER = ['procurement', 'production', 'installation'] as const;

const FALLBACK_BAR_COLOR = '#6B7280';

export const DEFAULT_GROUP_TIMELINE_FAMILY: GroupTimelineFamily = 'production';

export const GROUP_TIMELINE_FAMILY_STORAGE_KEY = 'pm-hub-group-timeline-family';

export interface GroupTimelineProjectLabel {
  projectNumber?: string | null;
  descriptor?: string | null;
}

export function isGroupTimelineFamily(value: string | null | undefined): value is GroupTimelineFamily {
  return !!value && (GROUP_TIMELINE_FAMILIES as readonly string[]).includes(value);
}

export function parseGroupTimelineFamily(value: string | null | undefined): GroupTimelineFamily | null {
  if (!value) return null;
  const n = value.trim().toLowerCase();
  return isGroupTimelineFamily(n) ? n : null;
}

export function readStoredGroupTimelineFamily(): GroupTimelineFamily {
  try {
    return parseGroupTimelineFamily(sessionStorage.getItem(GROUP_TIMELINE_FAMILY_STORAGE_KEY))
      ?? DEFAULT_GROUP_TIMELINE_FAMILY;
  } catch {
    return DEFAULT_GROUP_TIMELINE_FAMILY;
  }
}

export function storeGroupTimelineFamily(family: GroupTimelineFamily): void {
  try {
    sessionStorage.setItem(GROUP_TIMELINE_FAMILY_STORAGE_KEY, family);
  } catch {
    /* ignore quota / private-mode */
  }
}

export function isProcurementGroupName(name: string): boolean {
  return (name || '').trim().toLowerCase().includes('procurement');
}

export function familyFromGroupName(name: string): GroupTimelineFamily | null {
  for (const family of GROUP_TIMELINE_FAMILIES) {
    if (family === 'all') continue;
    if (groupNameMatchesFamily(name, family)) return family;
  }
  return null;
}

export function groupNameMatchesFamily(name: string, family: GroupTimelineFamily): boolean {
  switch (family) {
    case 'all':
      return true;
    case 'procurement':
      return isProcurementGroupName(name);
    case 'production':
      return isProductionGroupName(name);
    case 'installation':
      return isInstallGroupName(name);
    default:
      return false;
  }
}

export function resolveGroupTimelineSection(name: string): Omit<GroupTimelineSection, 'groupIds'> {
  const family = familyFromGroupName(name);
  if (family && family !== 'all') {
    return {
      id: family,
      name: GROUP_TIMELINE_FAMILY_LABELS[family],
      color: GROUP_TIMELINE_SECTION_COLORS[family] ?? FALLBACK_BAR_COLOR,
    };
  }
  const label = (name || '').trim() || 'Other';
  return {
    id: `other:${label.toLowerCase()}`,
    name: label,
    color: FALLBACK_BAR_COLOR,
  };
}

function sectionSortRank(sectionId: string, sectionName: string): number {
  const famIdx = (FAMILY_SECTION_ORDER as readonly string[]).indexOf(sectionId);
  if (famIdx >= 0) return famIdx;
  return 100 + sectionName.toLowerCase().charCodeAt(0) / 1000;
}

function buildAllTimelineSections(
  groups: TaskGroup[],
  sectionByGroupId: Map<string, Omit<GroupTimelineSection, 'groupIds'>>,
): GroupTimelineSection[] {
  const byId = new Map<string, GroupTimelineSection>();
  for (const g of groups) {
    if (!g.id) continue;
    const meta = sectionByGroupId.get(g.id) ?? resolveGroupTimelineSection(g.name);
    const existing = byId.get(meta.id);
    if (existing) {
      existing.groupIds.push(g.id);
    } else {
      byId.set(meta.id, { ...meta, groupIds: [g.id] });
    }
  }
  return Array.from(byId.values()).sort((a, b) => {
    const ra = sectionSortRank(a.id, a.name);
    const rb = sectionSortRank(b.id, b.name);
    if (ra !== rb) return ra - rb;
    return a.name.localeCompare(b.name);
  });
}

export function isGroupTimelineSupplierFocusFamily(
  family: GroupTimelineFamily,
): family is 'procurement' | 'production' {
  return family === 'procurement' || family === 'production';
}

const PROCUREMENT_FOCUS_NEEDLES = ['timber lead time', 'timber package lead'];
const PRODUCTION_FOCUS_NEEDLES = ['cut timber package'];

export function groupTimelineSupplierFocusLabel(family: GroupTimelineFamily): string {
  if (family === 'procurement') return 'Timber Lead Time';
  if (family === 'production') return 'Cut Timber Package';
  return '';
}

export function isGroupTimelineSupplierFocusTitle(
  title: string | undefined,
  family: GroupTimelineFamily,
): boolean {
  const n = (title || '').trim().toLowerCase();
  if (!n) return false;
  const needles = family === 'procurement'
    ? PROCUREMENT_FOCUS_NEEDLES
    : family === 'production'
      ? PRODUCTION_FOCUS_NEEDLES
      : [];
  return needles.some((needle) => n.includes(needle));
}

/** `#1234 Barn` — matches All Projects row numbering + descriptor. */
export function formatGroupTimelineSectionLabel(opts: {
  projectNumber?: string | null;
  descriptor?: string | null;
  projectName?: string | null;
  groupName?: string | null;
  disambiguate?: boolean;
}): string {
  const rawNum = (opts.projectNumber || '').trim();
  const numberDisplay = rawNum
    ? (rawNum.startsWith('#') ? rawNum : `#${rawNum}`)
    : '';
  const descriptor = (opts.descriptor || '').trim() || (opts.projectName || '').trim();
  const base = [numberDisplay, descriptor].filter(Boolean).join(' ').trim()
    || (opts.projectName || '').trim()
    || 'Project';
  if (opts.disambiguate && (opts.groupName || '').trim()) {
    return `${base} · ${opts.groupName!.trim()}`;
  }
  return base;
}

function taskStartMs(task: ProjectTask): number | null {
  const d = task.startDate ? new Date(task.startDate) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.getTime();
}

/** Distinct hues so neighboring projects don’t collide on the Gantt. */
export const GROUP_TIMELINE_PROJECT_PALETTE = [
  '#2563EB',
  '#DB2777',
  '#059669',
  '#D97706',
  '#7C3AED',
  '#0D9488',
  '#DC2626',
  '#4F46E5',
  '#CA8A04',
  '#0891B2',
  '#BE185D',
  '#15803D',
  '#C2410C',
  '#6D28D9',
  '#0369A1',
] as const;

export function projectColorForId(projectId: string): string {
  let h = 0;
  for (let i = 0; i < projectId.length; i++) {
    h = (h * 31 + projectId.charCodeAt(i)) >>> 0;
  }
  return GROUP_TIMELINE_PROJECT_PALETTE[h % GROUP_TIMELINE_PROJECT_PALETTE.length];
}

function earliestGroupStartMs(groupId: string, tasks: ProjectTask[]): number {
  let min = Number.POSITIVE_INFINITY;
  for (const t of tasks) {
    if (t.groupId !== groupId || t.parentTaskId) continue;
    const ms = taskStartMs(t);
    if (ms != null && ms < min) min = ms;
  }
  return min;
}

export const GROUP_TIMELINE_LAYOUT_MODES = ['project', 'group', 'supplier'] as const;
export type GroupTimelineLayoutMode = (typeof GROUP_TIMELINE_LAYOUT_MODES)[number];
export const GROUP_TIMELINE_LAYOUT_LABELS: Record<GroupTimelineLayoutMode, string> = {
  project: 'Project',
  group: 'Task group',
  supplier: 'Supplier',
};

export function groupTimelineProjectGroupId(projectId: string): string {
  return `__gt_project__:${projectId}`;
}

export function parseGroupTimelineProjectSectionId(sectionId: string | null | undefined): string | null {
  if (!sectionId) return null;
  const prefix = '__gt_project__:';
  return sectionId.startsWith(prefix) ? sectionId.slice(prefix.length) || null : null;
}
export const DEFAULT_GROUP_TIMELINE_LAYOUT: GroupTimelineLayoutMode = 'project';
export const GROUP_TIMELINE_LAYOUT_STORAGE_KEY = 'pm-hub-group-timeline-layout';

export const GROUP_TIMELINE_SORT_MODES = ['supplier', 'date'] as const;
export type GroupTimelineSortMode = (typeof GROUP_TIMELINE_SORT_MODES)[number];
export const GROUP_TIMELINE_SORT_LABELS: Record<GroupTimelineSortMode, string> = {
  supplier: 'Supplier',
  date: 'Date',
};
export const DEFAULT_GROUP_TIMELINE_SORT: GroupTimelineSortMode = 'supplier';
export const GROUP_TIMELINE_SORT_STORAGE_KEY = 'pm-hub-group-timeline-sort';

export function parseGroupTimelineLayoutMode(value: string | null | undefined): GroupTimelineLayoutMode | null {
  if (!value) return null;
  const n = value.trim().toLowerCase();
  return (GROUP_TIMELINE_LAYOUT_MODES as readonly string[]).includes(n) ? n as GroupTimelineLayoutMode : null;
}

export function parseGroupTimelineSortMode(value: string | null | undefined): GroupTimelineSortMode | null {
  if (!value) return null;
  const n = value.trim().toLowerCase();
  return (GROUP_TIMELINE_SORT_MODES as readonly string[]).includes(n) ? n as GroupTimelineSortMode : null;
}

export function readStoredGroupTimelineLayout(): GroupTimelineLayoutMode {
  try {
    return parseGroupTimelineLayoutMode(sessionStorage.getItem(GROUP_TIMELINE_LAYOUT_STORAGE_KEY))
      ?? DEFAULT_GROUP_TIMELINE_LAYOUT;
  } catch {
    return DEFAULT_GROUP_TIMELINE_LAYOUT;
  }
}

export function storeGroupTimelineLayout(mode: GroupTimelineLayoutMode): void {
  try {
    sessionStorage.setItem(GROUP_TIMELINE_LAYOUT_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function readStoredGroupTimelineSort(): GroupTimelineSortMode {
  try {
    return parseGroupTimelineSortMode(sessionStorage.getItem(GROUP_TIMELINE_SORT_STORAGE_KEY))
      ?? DEFAULT_GROUP_TIMELINE_SORT;
  } catch {
    return DEFAULT_GROUP_TIMELINE_SORT;
  }
}

export function storeGroupTimelineSort(mode: GroupTimelineSortMode): void {
  try {
    sessionStorage.setItem(GROUP_TIMELINE_SORT_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export const GROUP_TIMELINE_EXTRA_TASKS_STORAGE_KEY = 'pm-hub-group-timeline-extra-tasks';

export type GroupTimelineExtraTasksByFamily = Partial<Record<GroupTimelineFamily, string[]>>;

function parseExtraTaskMap(raw: string | null): GroupTimelineExtraTasksByFamily {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: GroupTimelineExtraTasksByFamily = {};
    for (const family of GROUP_TIMELINE_FAMILIES) {
      const ids = (parsed as Record<string, unknown>)[family];
      if (!Array.isArray(ids)) continue;
      out[family] = ids.filter((id): id is string => typeof id === 'string' && !!id);
    }
    return out;
  } catch {
    return {};
  }
}

export function readStoredGroupTimelineExtraTasks(): GroupTimelineExtraTasksByFamily {
  try {
    return parseExtraTaskMap(sessionStorage.getItem(GROUP_TIMELINE_EXTRA_TASKS_STORAGE_KEY));
  } catch {
    return {};
  }
}

export function storeGroupTimelineExtraTasks(map: GroupTimelineExtraTasksByFamily): void {
  try {
    sessionStorage.setItem(GROUP_TIMELINE_EXTRA_TASKS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function collectFamilyParentTasks(params: {
  family: GroupTimelineFamily;
  projects: TimberProject[];
  groups: TaskGroup[];
  tasks: ProjectTask[];
}): { matchingGroups: TaskGroup[]; topLevel: ProjectTask[] } {
  const activeProjects = params.projects.filter((p) => p.id && p.status !== 'completed');
  const projectIds = new Set(activeProjects.map((p) => p.id!).filter(Boolean));
  const matchingGroups = params.groups.filter(
    (g) => g.id && projectIds.has(g.projectId) && groupNameMatchesFamily(g.name, params.family),
  );
  const matchingGroupIds = new Set(matchingGroups.map((g) => g.id!));
  const topLevel = params.tasks.filter(
    (t) =>
      !t.parentTaskId
      && t.groupId
      && matchingGroupIds.has(t.groupId)
      && projectIds.has(t.projectId),
  );
  return { matchingGroups, topLevel };
}

export function listGroupTimelineFamilyParentTasks(params: {
  family: GroupTimelineFamily;
  projects: TimberProject[];
  groups: TaskGroup[];
  tasks: ProjectTask[];
}): ProjectTask[] {
  return collectFamilyParentTasks(params).topLevel;
}

function applySupplierFocusFilter(
  family: GroupTimelineFamily,
  topLevel: ProjectTask[],
  allTasks: ProjectTask[],
  extraTaskIds?: string[],
): ProjectTask[] {
  const extras = new Set(extraTaskIds ?? []);
  const kept = isGroupTimelineSupplierFocusFamily(family)
    ? topLevel.filter((t) =>
      isGroupTimelineSupplierFocusTitle(t.title, family) || (!!t.id && extras.has(t.id)),
    )
    : topLevel;
  const keptIds = new Set(kept.map((t) => t.id).filter((id): id is string => !!id));
  const subtasks = allTasks.filter((t) => t.parentTaskId && keptIds.has(t.parentTaskId));
  return [...kept, ...subtasks];
}

function supplierKeysForTask(
  task: ProjectTask,
  byTaskId?: Map<string, GroupTimelineSupplierResolution>,
): string[] {
  if (!task.id || !byTaskId) return [UNASSIGNED_SUPPLIER_KEY];
  const res = byTaskId.get(task.id);
  if (!res?.orgIds.length) return [UNASSIGNED_SUPPLIER_KEY];
  return res.orgIds.filter(Boolean);
}

function projectLabelForTask(
  task: ProjectTask,
  projectById: Map<string, TimberProject>,
  projectLabels?: Map<string, GroupTimelineProjectLabel>,
): string {
  const project = projectById.get(task.projectId);
  const label = projectLabels?.get(task.projectId);
  return formatGroupTimelineSectionLabel({
    projectNumber: label?.projectNumber,
    descriptor: label?.descriptor,
    projectName: project?.name,
  });
}

export function buildGroupTimelineView(params: {
  family: GroupTimelineFamily;
  projects: TimberProject[];
  groups: TaskGroup[];
  tasks: ProjectTask[];
  projectLabels?: Map<string, GroupTimelineProjectLabel>;
  layout?: GroupTimelineLayoutMode;
  sortMode?: GroupTimelineSortMode;
  supplierByTaskId?: Map<string, GroupTimelineSupplierResolution>;
  supplierNameByOrgId?: Map<string, string>;
  extraTaskIds?: string[];
}): { groups: TaskGroup[]; tasks: ProjectTask[]; sections: GroupTimelineSection[] } {
  const {
    family,
    projects,
    groups,
    tasks,
    projectLabels,
    layout = 'project',
    sortMode = 'supplier',
    supplierByTaskId,
    supplierNameByOrgId = new Map(),
    extraTaskIds,
  } = params;
  const activeProjects = projects.filter((p) => p.id && p.status !== 'completed');
  const projectById = new Map(activeProjects.filter((p) => p.id).map((p) => [p.id!, p]));

  const { matchingGroups, topLevel } = collectFamilyParentTasks({
    family,
    projects,
    groups,
    tasks,
  });
  const familyTasks = applySupplierFocusFilter(family, topLevel, tasks, extraTaskIds);

  const visibleGroupIds = new Set(
    familyTasks.filter((t) => !t.parentTaskId && t.groupId).map((t) => t.groupId!),
  );
  const groupsForView = (isGroupTimelineSupplierFocusFamily(family) || family === 'all')
    ? matchingGroups.filter((g) => g.id && visibleGroupIds.has(g.id))
    : matchingGroups;

  if (layout === 'supplier') {
    return {
      ...buildSupplierLayoutView({
        familyTasks,
        projectById,
        projectLabels,
        supplierByTaskId,
        supplierNameByOrgId,
      }),
      sections: [],
    };
  }

  if (layout === 'project') {
    return buildProjectLayoutView({
      family,
      familyTasks,
      groupsForView,
      projectById,
      projectLabels,
      sortMode,
      supplierByTaskId,
      supplierNameByOrgId,
    });
  }

  return buildTaskGroupLayoutView({
    family,
    familyTasks,
    groupsForView,
    projectById,
    projectLabels,
    sortMode,
    supplierByTaskId,
    supplierNameByOrgId,
  });
}

function compareTimelineGroups(
  a: TaskGroup,
  b: TaskGroup,
  familyTasks: ProjectTask[],
  sortMode: GroupTimelineSortMode,
  supplierByTaskId: Map<string, GroupTimelineSupplierResolution> | undefined,
  supplierNameByOrgId: Map<string, string>,
): number {
  if (sortMode === 'supplier') {
    const aTasks = familyTasks.filter((t) => t.groupId === a.id);
    const bTasks = familyTasks.filter((t) => t.groupId === b.id);
    const aOrg = primarySupplierForTasks(aTasks, supplierByTaskId ?? new Map());
    const bOrg = primarySupplierForTasks(bTasks, supplierByTaskId ?? new Map());
    const aUn = !aOrg;
    const bUn = !bOrg;
    if (aUn !== bUn) return aUn ? 1 : -1;
    const aName = orgNameFromMap(aOrg, supplierNameByOrgId).toLowerCase();
    const bName = orgNameFromMap(bOrg, supplierNameByOrgId).toLowerCase();
    if (aName !== bName) return aName.localeCompare(bName);
  }
  const aStart = earliestGroupStartMs(a.id!, familyTasks);
  const bStart = earliestGroupStartMs(b.id!, familyTasks);
  if (aStart !== bStart) return aStart - bStart;
  const aLabel = a.name.toLowerCase();
  const bLabel = b.name.toLowerCase();
  if (aLabel !== bLabel) return aLabel.localeCompare(bLabel);
  return (a.position || 0) - (b.position || 0);
}

function compareProjectsBySort(
  projectIdA: string,
  projectIdB: string,
  familyTasks: ProjectTask[],
  sortMode: GroupTimelineSortMode,
  supplierByTaskId: Map<string, GroupTimelineSupplierResolution> | undefined,
  supplierNameByOrgId: Map<string, string>,
  projectById: Map<string, TimberProject>,
  projectLabels?: Map<string, GroupTimelineProjectLabel>,
): number {
  const aTasks = familyTasks.filter((t) => t.projectId === projectIdA);
  const bTasks = familyTasks.filter((t) => t.projectId === projectIdB);
  if (sortMode === 'supplier') {
    const aOrg = primarySupplierForTasks(aTasks, supplierByTaskId ?? new Map());
    const bOrg = primarySupplierForTasks(bTasks, supplierByTaskId ?? new Map());
    const aUn = !aOrg;
    const bUn = !bOrg;
    if (aUn !== bUn) return aUn ? 1 : -1;
    const aName = orgNameFromMap(aOrg, supplierNameByOrgId).toLowerCase();
    const bName = orgNameFromMap(bOrg, supplierNameByOrgId).toLowerCase();
    if (aName !== bName) return aName.localeCompare(bName);
  }
  let aStart = Number.POSITIVE_INFINITY;
  let bStart = Number.POSITIVE_INFINITY;
  for (const t of aTasks) {
    if (t.parentTaskId) continue;
    const ms = taskStartMs(t);
    if (ms != null && ms < aStart) aStart = ms;
  }
  for (const t of bTasks) {
    if (t.parentTaskId) continue;
    const ms = taskStartMs(t);
    if (ms != null && ms < bStart) bStart = ms;
  }
  if (aStart !== bStart) return aStart - bStart;
  const aLabel = formatGroupTimelineSectionLabel({
    projectNumber: projectLabels?.get(projectIdA)?.projectNumber,
    descriptor: projectLabels?.get(projectIdA)?.descriptor,
    projectName: projectById.get(projectIdA)?.name,
  }).toLowerCase();
  const bLabel = formatGroupTimelineSectionLabel({
    projectNumber: projectLabels?.get(projectIdB)?.projectNumber,
    descriptor: projectLabels?.get(projectIdB)?.descriptor,
    projectName: projectById.get(projectIdB)?.name,
  }).toLowerCase();
  return aLabel.localeCompare(bLabel);
}

function buildAllProjectNestedView(params: {
  familyTasks: ProjectTask[];
  groupsForView: TaskGroup[];
  projectById: Map<string, TimberProject>;
  projectLabels?: Map<string, GroupTimelineProjectLabel>;
  sortMode: GroupTimelineSortMode;
  supplierByTaskId?: Map<string, GroupTimelineSupplierResolution>;
  supplierNameByOrgId: Map<string, string>;
}): { groups: TaskGroup[]; tasks: ProjectTask[]; sections: GroupTimelineSection[] } {
  const {
    familyTasks,
    groupsForView,
    projectById,
    projectLabels,
    sortMode,
    supplierByTaskId,
    supplierNameByOrgId,
  } = params;

  const origByProject = new Map<string, TaskGroup[]>();
  for (const g of groupsForView) {
    const list = origByProject.get(g.projectId) ?? [];
    list.push(g);
    origByProject.set(g.projectId, list);
  }

  const projectOrder = Array.from(origByProject.keys()).sort((a, b) =>
    compareProjectsBySort(
      a,
      b,
      familyTasks,
      sortMode,
      supplierByTaskId,
      supplierNameByOrgId,
      projectById,
      projectLabels,
    ),
  );

  const orderedGroups: TaskGroup[] = [];
  const sections: GroupTimelineSection[] = [];
  for (const projectId of projectOrder) {
    const origGroups = [...(origByProject.get(projectId) ?? [])].sort((a, b) => {
      const aSec = resolveGroupTimelineSection(a.name);
      const bSec = resolveGroupTimelineSection(b.name);
      const ra = sectionSortRank(aSec.id, aSec.name);
      const rb = sectionSortRank(bSec.id, bSec.name);
      if (ra !== rb) return ra - rb;
      const nameCmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      if (nameCmp !== 0) return nameCmp;
      return (a.position || 0) - (b.position || 0);
    });
    const project = projectById.get(projectId);
    const label = projectLabels?.get(projectId);
    sections.push({
      id: groupTimelineProjectGroupId(projectId),
      name: formatGroupTimelineSectionLabel({
        projectNumber: label?.projectNumber,
        descriptor: label?.descriptor,
        projectName: project?.name,
      }),
      color: projectColorForId(projectId),
      groupIds: origGroups.map((g) => g.id!).filter(Boolean),
    });
    orderedGroups.push(...origGroups);
  }

  return {
    groups: orderedGroups.map((g, i) => ({ ...g, position: i + 1 })),
    tasks: familyTasks,
    sections,
  };
}

function buildProjectLayoutView(params: {
  family: GroupTimelineFamily;
  familyTasks: ProjectTask[];
  groupsForView: TaskGroup[];
  projectById: Map<string, TimberProject>;
  projectLabels?: Map<string, GroupTimelineProjectLabel>;
  sortMode: GroupTimelineSortMode;
  supplierByTaskId?: Map<string, GroupTimelineSupplierResolution>;
  supplierNameByOrgId: Map<string, string>;
}): { groups: TaskGroup[]; tasks: ProjectTask[]; sections: GroupTimelineSection[] } {
  const {
    family,
    familyTasks,
    groupsForView,
    projectById,
    projectLabels,
    sortMode,
    supplierByTaskId,
    supplierNameByOrgId,
  } = params;

  if (family === 'all') {
    return buildAllProjectNestedView({
      familyTasks,
      groupsForView,
      projectById,
      projectLabels,
      sortMode,
      supplierByTaskId,
      supplierNameByOrgId,
    });
  }

  const origByProject = new Map<string, TaskGroup[]>();
  for (const g of groupsForView) {
    const list = origByProject.get(g.projectId) ?? [];
    list.push(g);
    origByProject.set(g.projectId, list);
  }

  const remap = new Map<string, string>();
  const merged: TaskGroup[] = [];
  for (const [projectId, origGroups] of Array.from(origByProject.entries())) {
    const synthId = groupTimelineProjectGroupId(projectId);
    const project = projectById.get(projectId);
    const label = projectLabels?.get(projectId);
    merged.push({
      id: synthId,
      projectId,
      name: formatGroupTimelineSectionLabel({
        projectNumber: label?.projectNumber,
        descriptor: label?.descriptor,
        projectName: project?.name,
      }),
      color: origGroups[0]?.color || projectColorForId(projectId),
      position: origGroups[0]?.position || 0,
    });
    for (const g of origGroups) {
      if (g.id) remap.set(g.id, synthId);
    }
  }

  const remappedTasks = familyTasks.map((t) => ({
    ...t,
    groupId: t.groupId && remap.has(t.groupId) ? remap.get(t.groupId)! : t.groupId,
  }));

  merged.sort((a, b) =>
    compareTimelineGroups(a, b, remappedTasks, sortMode, supplierByTaskId, supplierNameByOrgId),
  );
  return {
    groups: merged.map((g, i) => ({ ...g, position: i + 1 })),
    tasks: remappedTasks,
    sections: [],
  };
}

function buildTaskGroupLayoutView(params: {
  family: GroupTimelineFamily;
  familyTasks: ProjectTask[];
  groupsForView: TaskGroup[];
  projectById: Map<string, TimberProject>;
  projectLabels?: Map<string, GroupTimelineProjectLabel>;
  sortMode: GroupTimelineSortMode;
  supplierByTaskId?: Map<string, GroupTimelineSupplierResolution>;
  supplierNameByOrgId: Map<string, string>;
}): { groups: TaskGroup[]; tasks: ProjectTask[]; sections: GroupTimelineSection[] } {
  const {
    family,
    familyTasks,
    groupsForView,
    projectById,
    projectLabels,
    sortMode,
    supplierByTaskId,
    supplierNameByOrgId,
  } = params;

  const sectionByGroupId = new Map(
    groupsForView
      .filter((g) => g.id)
      .map((g) => [g.id!, resolveGroupTimelineSection(g.name)] as const),
  );

  const matchCountByProject = new Map<string, number>();
  for (const g of groupsForView) {
    matchCountByProject.set(g.projectId, (matchCountByProject.get(g.projectId) || 0) + 1);
  }

  const labeled = groupsForView.map((g) => {
    const project = projectById.get(g.projectId);
    const label = projectLabels?.get(g.projectId);
    const name = formatGroupTimelineSectionLabel({
      projectNumber: label?.projectNumber,
      descriptor: label?.descriptor,
      projectName: project?.name,
      groupName: g.name,
      disambiguate: (matchCountByProject.get(g.projectId) || 0) > 1,
    });
    return { ...g, name };
  });

  labeled.sort((a, b) => {
    if (family === 'all') {
      const aSec = sectionByGroupId.get(a.id!) ?? resolveGroupTimelineSection(a.name);
      const bSec = sectionByGroupId.get(b.id!) ?? resolveGroupTimelineSection(b.name);
      const ra = sectionSortRank(aSec.id, aSec.name);
      const rb = sectionSortRank(bSec.id, bSec.name);
      if (ra !== rb) return ra - rb;
      const nameCmp = aSec.name.localeCompare(bSec.name);
      if (nameCmp !== 0) return nameCmp;
    }
    return compareTimelineGroups(
      a,
      b,
      familyTasks,
      sortMode,
      supplierByTaskId,
      supplierNameByOrgId,
    );
  });

  const positioned = labeled.map((g, i) => ({ ...g, position: i + 1 }));
  const sections = family === 'all'
    ? buildAllTimelineSections(positioned, sectionByGroupId)
    : [];
  return { groups: positioned, tasks: familyTasks, sections };
}

function displayTaskIdForSupplier(realId: string, orgKey: string, keys: string[]): string {
  return keys.length > 1 ? supplierDisplayTaskId(realId, orgKey) : realId;
}

function buildSupplierLayoutView(params: {
  familyTasks: ProjectTask[];
  projectById: Map<string, TimberProject>;
  projectLabels?: Map<string, GroupTimelineProjectLabel>;
  supplierByTaskId?: Map<string, GroupTimelineSupplierResolution>;
  supplierNameByOrgId: Map<string, string>;
}): { groups: TaskGroup[]; tasks: ProjectTask[] } {
  const { familyTasks, projectById, projectLabels, supplierByTaskId, supplierNameByOrgId } = params;
  const keysByTaskId = new Map<string, string[]>();
  for (const t of familyTasks) {
    if (!t.id) continue;
    keysByTaskId.set(t.id, supplierKeysForTask(t, supplierByTaskId));
  }
  for (const t of familyTasks) {
    if (!t.id || !t.parentTaskId) continue;
    const own = keysByTaskId.get(t.id);
    if (own && own.length === 1 && own[0] === UNASSIGNED_SUPPLIER_KEY) {
      const parentKeys = keysByTaskId.get(t.parentTaskId);
      if (parentKeys?.length) keysByTaskId.set(t.id, parentKeys);
    }
  }

  const keys = new Set<string>();
  const tasks: ProjectTask[] = [];
  for (const t of familyTasks) {
    if (!t.id) continue;
    const orgKeys = keysByTaskId.get(t.id) ?? [UNASSIGNED_SUPPLIER_KEY];
    const projectLabel = projectLabelForTask(t, projectById, projectLabels);
    const title = t.parentTaskId ? t.title : `${projectLabel} · ${t.title}`;
    for (const key of orgKeys) {
      keys.add(key);
      let parentTaskId = t.parentTaskId;
      if (parentTaskId) {
        const parentKeys = keysByTaskId.get(parentTaskId) ?? [UNASSIGNED_SUPPLIER_KEY];
        if (parentKeys.includes(key)) {
          parentTaskId = displayTaskIdForSupplier(parentTaskId, key, parentKeys);
        } else {
          parentTaskId = undefined;
        }
      }
      tasks.push({
        ...t,
        id: displayTaskIdForSupplier(t.id, key, orgKeys),
        parentTaskId,
        groupId: supplierGroupId(key),
        title,
      });
    }
  }

  const orderedKeys = Array.from(keys).sort((a, b) => {
    if (a === UNASSIGNED_SUPPLIER_KEY) return 1;
    if (b === UNASSIGNED_SUPPLIER_KEY) return -1;
    return orgNameFromMap(a, supplierNameByOrgId).localeCompare(orgNameFromMap(b, supplierNameByOrgId));
  });

  const groups: TaskGroup[] = orderedKeys.map((key, i) => {
    const sample = tasks.find((t) => t.groupId === supplierGroupId(key));
    return {
      id: supplierGroupId(key),
      projectId: sample?.projectId || '',
      name: orgNameFromMap(key, supplierNameByOrgId),
      color: key === UNASSIGNED_SUPPLIER_KEY ? FALLBACK_BAR_COLOR : projectColorForId(key),
      position: i + 1,
    };
  });

  return { groups, tasks };
}

export const GROUP_TIMELINE_COLOR_MODES = [
  'group',
  'project',
  'supplier',
  'status',
  'department',
  'priority',
  'phase',
] as const;

export type GroupTimelineColorMode = (typeof GROUP_TIMELINE_COLOR_MODES)[number];

export const GROUP_TIMELINE_COLOR_MODE_LABELS: Record<GroupTimelineColorMode, string> = {
  group: 'Group',
  project: 'Project',
  supplier: 'Supplier',
  status: 'Status',
  department: 'Department',
  priority: 'Priority',
  phase: 'Phase',
};

export const DEFAULT_GROUP_TIMELINE_COLOR_MODE: GroupTimelineColorMode = 'project';

export const GROUP_TIMELINE_COLOR_MODE_STORAGE_KEY = 'pm-hub-group-timeline-color-mode';

export const GROUP_TIMELINE_STATUS_COLORS: Record<TaskStatus, string> = {
  pending: '#94A3B8',
  'in-progress': '#3B82F6',
  review: '#F59E0B',
  completed: '#22C55E',
  blocked: '#EF4444',
  'on-hold': '#7C3AED',
};

export const GROUP_TIMELINE_STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'Not Started',
  'in-progress': 'In Progress',
  review: 'Review',
  completed: 'Completed',
  blocked: 'Blocked',
  'on-hold': 'On Hold',
};

export const GROUP_TIMELINE_PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: '#94A3B8',
  medium: '#3B82F6',
  high: '#F59E0B',
  urgent: '#EF4444',
};

export const GROUP_TIMELINE_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

export const GROUP_TIMELINE_PHASE_COLORS: Record<ProjectPhase, string> = {
  estimating: '#D97706',
  preconstruction: '#7C3AED',
  contracted: '#2563EB',
};

export function isGroupTimelineColorMode(value: string | null | undefined): value is GroupTimelineColorMode {
  return !!value && (GROUP_TIMELINE_COLOR_MODES as readonly string[]).includes(value);
}

export function parseGroupTimelineColorMode(value: string | null | undefined): GroupTimelineColorMode | null {
  if (!value) return null;
  const n = value.trim().toLowerCase();
  return isGroupTimelineColorMode(n) ? n : null;
}

export function readStoredGroupTimelineColorMode(): GroupTimelineColorMode {
  try {
    return parseGroupTimelineColorMode(sessionStorage.getItem(GROUP_TIMELINE_COLOR_MODE_STORAGE_KEY))
      ?? DEFAULT_GROUP_TIMELINE_COLOR_MODE;
  } catch {
    return DEFAULT_GROUP_TIMELINE_COLOR_MODE;
  }
}

export function storeGroupTimelineColorMode(mode: GroupTimelineColorMode): void {
  try {
    sessionStorage.setItem(GROUP_TIMELINE_COLOR_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore quota / private-mode */
  }
}

export interface GroupTimelineColorContext {
  groups: TaskGroup[];
  /** Project phase for color-by-phase (and leftover fallbacks). */
  projectPhaseById?: Map<string, ProjectPhase | undefined>;
  supplierByTaskId?: Map<string, GroupTimelineSupplierResolution>;
  supplierNameByOrgId?: Map<string, string>;
  /** Admin palette from settings/group_timeline_colors (All-projects Group color mode). */
  canonicalColorEntries?: GroupTimelineColorEntry[];
}

export interface GroupTimelineLegendItem {
  key: string;
  label: string;
  color: string;
}

export function resolveGroupTimelineBarColor(
  task: ProjectTask,
  mode: GroupTimelineColorMode,
  ctx: GroupTimelineColorContext,
): string {
  switch (mode) {
    case 'project':
      return task.projectId ? projectColorForId(task.projectId) : FALLBACK_BAR_COLOR;
    case 'supplier': {
      const fromGroup = parseSupplierGroupId(task.groupId);
      if (fromGroup && fromGroup !== UNASSIGNED_SUPPLIER_KEY && fromGroup !== MULTIPLE_SUPPLIER_KEY) {
        return projectColorForId(fromGroup);
      }
      const realId = unwrapSupplierDisplayTaskId(task.id);
      const res = realId ? ctx.supplierByTaskId?.get(realId) : undefined;
      if (!res?.primaryOrgId) return FALLBACK_BAR_COLOR;
      return projectColorForId(res.primaryOrgId);
    }
    case 'status':
      return GROUP_TIMELINE_STATUS_COLORS[task.status] || FALLBACK_BAR_COLOR;
    case 'department':
      return DEPARTMENT_SWATCH_COLORS[task.department] || FALLBACK_BAR_COLOR;
    case 'priority':
      return GROUP_TIMELINE_PRIORITY_COLORS[task.priority] || FALLBACK_BAR_COLOR;
    case 'phase': {
      const phase = ctx.projectPhaseById?.get(task.projectId) ?? 'contracted';
      return GROUP_TIMELINE_PHASE_COLORS[phase] || FALLBACK_BAR_COLOR;
    }
    case 'group':
    default: {
      const group = ctx.groups.find((g) => g.id === task.groupId);
      const canonical = resolveCanonicalTimelineColor({
        taskTitle: task.title,
        groupName: group?.name,
        entries: ctx.canonicalColorEntries ?? [],
      });
      if (canonical) return canonical;
      return group?.color || FALLBACK_BAR_COLOR;
    }
  }
}

const STATUS_LEGEND_ORDER: TaskStatus[] = [
  'pending',
  'in-progress',
  'review',
  'completed',
  'blocked',
  'on-hold',
];
const DEPT_LEGEND_ORDER: ProjectDepartment[] = [
  'preconstruction',
  'design',
  'procurement',
  'production',
  'installation',
];
const PRIORITY_LEGEND_ORDER: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];
const PHASE_LEGEND_ORDER: ProjectPhase[] = ['estimating', 'preconstruction', 'contracted'];

/** Compact legend for categorical modes. Project/group use the left-panel dots instead. */
export function buildGroupTimelineColorLegend(
  mode: GroupTimelineColorMode,
  tasks: ProjectTask[],
  ctx: GroupTimelineColorContext,
): GroupTimelineLegendItem[] {
  if (mode === 'group' || mode === 'project') return [];

  if (mode === 'supplier') {
    const names = ctx.supplierNameByOrgId ?? new Map();
    const seen = new Set<string>();
    let hasUnassigned = false;
    for (const t of tasks) {
      const fromGroup = parseSupplierGroupId(t.groupId);
      if (fromGroup === UNASSIGNED_SUPPLIER_KEY) {
        hasUnassigned = true;
        continue;
      }
      if (fromGroup && fromGroup !== MULTIPLE_SUPPLIER_KEY) {
        seen.add(fromGroup);
        continue;
      }
      const realId = unwrapSupplierDisplayTaskId(t.id);
      const res = realId ? ctx.supplierByTaskId?.get(realId) : undefined;
      if (!res?.primaryOrgId) {
        hasUnassigned = true;
        continue;
      }
      seen.add(res.primaryOrgId);
    }
    const items = Array.from(seen)
      .sort((a, b) => orgNameFromMap(a, names).localeCompare(orgNameFromMap(b, names)))
      .map((id) => ({
        key: id,
        label: orgNameFromMap(id, names),
        color: projectColorForId(id),
      }));
    if (hasUnassigned) {
      items.push({ key: UNASSIGNED_SUPPLIER_KEY, label: UNASSIGNED_SUPPLIER_LABEL, color: FALLBACK_BAR_COLOR });
    }
    return items;
  }

  const seen = new Set<string>();
  for (const t of tasks) {
    if (mode === 'status') seen.add(t.status);
    else if (mode === 'department') seen.add(t.department);
    else if (mode === 'priority') seen.add(t.priority);
    else if (mode === 'phase') seen.add(ctx.projectPhaseById?.get(t.projectId) ?? 'contracted');
  }

  if (mode === 'status') {
    return STATUS_LEGEND_ORDER.filter((s) => seen.has(s)).map((s) => ({
      key: s,
      label: GROUP_TIMELINE_STATUS_LABELS[s],
      color: GROUP_TIMELINE_STATUS_COLORS[s],
    }));
  }
  if (mode === 'department') {
    return DEPT_LEGEND_ORDER.filter((d) => seen.has(d)).map((d) => ({
      key: d,
      label: DEPARTMENT_DISPLAY_LABELS[d],
      color: DEPARTMENT_SWATCH_COLORS[d],
    }));
  }
  if (mode === 'priority') {
    return PRIORITY_LEGEND_ORDER.filter((p) => seen.has(p)).map((p) => ({
      key: p,
      label: GROUP_TIMELINE_PRIORITY_LABELS[p],
      color: GROUP_TIMELINE_PRIORITY_COLORS[p],
    }));
  }
  return PHASE_LEGEND_ORDER.filter((p) => seen.has(p)).map((p) => ({
    key: p,
    label: PROJECT_PHASE_LABELS[p],
    color: GROUP_TIMELINE_PHASE_COLORS[p],
  }));
}

export function applyGroupTimelineGroupColors(
  groups: TaskGroup[],
  mode: GroupTimelineColorMode,
  opts?: {
    tasks?: ProjectTask[];
    supplierByTaskId?: Map<string, GroupTimelineSupplierResolution>;
    canonicalColorEntries?: GroupTimelineColorEntry[];
  },
): TaskGroup[] {
  if (mode === 'project') {
    return groups.map((g) => ({
      ...g,
      color: g.projectId ? projectColorForId(g.projectId) : FALLBACK_BAR_COLOR,
    }));
  }
  if (mode === 'supplier') {
    return groups.map((g) => {
      const orgKey = parseSupplierGroupId(g.id);
      if (orgKey) {
        if (orgKey === UNASSIGNED_SUPPLIER_KEY || orgKey === MULTIPLE_SUPPLIER_KEY) {
          return { ...g, color: FALLBACK_BAR_COLOR };
        }
        return { ...g, color: projectColorForId(orgKey) };
      }
      const sectionTasks = (opts?.tasks ?? []).filter((t) => t.groupId === g.id);
      const primary = primarySupplierForTasks(sectionTasks, opts?.supplierByTaskId ?? new Map());
      return { ...g, color: primary ? projectColorForId(primary) : FALLBACK_BAR_COLOR };
    });
  }
  if (mode === 'group') {
    const entries = opts?.canonicalColorEntries ?? [];
    return groups.map(g => {
      const color =
        resolveCanonicalTimelineColor({ groupName: g.name, entries })
        ?? g.color
        ?? FALLBACK_BAR_COLOR;
      return { ...g, color };
    });
  }
  return groups;
}
